import { useEffect, useState } from 'react';
import {
  adminApi,
  type BatchSummary,
  type BatchTrainerListItem,
  type TrainerPoolEntry,
} from '@/lib/api/admin';
import { toast } from 'sonner';
import { formatDate } from '@/lib/date';
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const statusColor: Record<string, string> = {
  active: 'border-emerald-500 text-emerald-700 bg-emerald-50',
  ended: 'border-gray-300 text-gray-600 bg-gray-100',
  cancelled: 'border-gray-300 text-gray-600 bg-gray-100',
};

export function ManageTrainersDrawer({
  batch,
  onClose,
  onChanged,
}: {
  batch: BatchSummary;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<{ trainers: BatchTrainerListItem[] } | null>(null);
  const [pool, setPool] = useState<TrainerPoolEntry[]>([]);
  const [pickedUserID, setPickedUserID] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [endingTrainer, setEndingTrainer] = useState<BatchTrainerListItem | null>(null);

  const reload = async () => {
    try {
      const d = await adminApi.getBatch(batch.id);
      setDetail({ trainers: d.trainers });
      const p = await adminApi.listTrainerPool();
      setPool(p);
      setPickedUserID('');
      setStartDate('');
      setEndDate('');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to load assignments');
    }
  };

  useEffect(() => { reload(); }, [batch.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pickedUserID) return;
    setBusy(true);
    try {
      await adminApi.assignTrainer(batch.id, {
        user_id: pickedUserID,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      toast.success('Trainer assigned');
      await reload();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to assign');
    } finally {
      setBusy(false);
    }
  };

  const endAssignment = async () => {
    if (!endingTrainer) return;
    setBusy(true);
    try {
      await adminApi.updateAssignment(batch.id, endingTrainer.user_id, { status: 'ended' });
      toast.success('Assignment ended');
      setEndingTrainer(null);
      await reload();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to end');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="flex w-[480px] flex-col p-0 sm:max-w-[480px]">
        <SheetHeader className="border-b border-border px-5 py-4">
          <SheetTitle className="text-[15px]">Trainers — {batch.name}</SheetTitle>
          <SheetDescription className="text-[12px]">{batch.org_name}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <section>
            <h4 className="mb-2 text-[13px] font-semibold text-foreground">Current assignments</h4>
            {!detail ? (
              <p className="text-[13px] text-muted-foreground">Loading…</p>
            ) : detail.trainers.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">No assignments yet.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-[11px] uppercase tracking-wider">Trainer</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-wider">Period</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-wider">Status</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.trainers.map((t) => (
                      <TableRow key={t.user_id}>
                        <TableCell className="py-2.5 text-[13px]">
                          <div className="font-medium text-foreground">{t.username}</div>
                          <div className="text-[11px] text-muted-foreground">{t.email}</div>
                        </TableCell>
                        <TableCell className="py-2.5 text-[12px] tabular-nums text-muted-foreground">
                          {formatDate(t.start_date)} → {formatDate(t.end_date)}
                        </TableCell>
                        <TableCell className="py-2.5">
                          <Badge
                            variant="outline"
                            className={`text-[11px] capitalize ${statusColor[t.status] ?? statusColor.ended}`}
                          >
                            {t.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2.5 text-right">
                          {t.status === 'active' && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={busy}
                              onClick={() => setEndingTrainer(t)}
                              className="text-[11px]"
                            >
                              End
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          <section>
            <h4 className="mb-2 text-[13px] font-semibold text-foreground">Add trainer</h4>
            {pool.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">
                No trainers in the pool yet. Invite one via{' '}
                <code className="text-[11px]">POST /api/v1/invites</code> with role=trainer.
              </p>
            ) : (
              <form onSubmit={assign} className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-foreground">Trainer</label>
                  <Select value={pickedUserID} onValueChange={setPickedUserID}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select trainer…" />
                    </SelectTrigger>
                    <SelectContent>
                      {pool.map((p) => (
                        <SelectItem key={p.user_id} value={p.user_id}>
                          {p.username} ({p.email}) — {p.active_assignments} active
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-foreground">Start date</label>
                    <DatePicker value={startDate} onChange={setStartDate} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-foreground">End date</label>
                    <DatePicker value={endDate} onChange={setEndDate} />
                  </div>
                </div>
                <Button type="submit" disabled={busy || !pickedUserID}>
                  {busy ? 'Assigning…' : 'Assign'}
                </Button>
              </form>
            )}
          </section>
        </div>

        <div className="border-t border-border px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </div>
      </SheetContent>

      <AlertDialog open={!!endingTrainer} onOpenChange={(open) => !open && setEndingTrainer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will end <span className="font-medium text-foreground">{endingTrainer?.username}</span>'s assignment to this batch. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={endAssignment}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? 'Ending…' : 'End assignment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
